import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Camera, Save } from 'lucide-react';

interface ProfileData {
  full_name: string;
  bio: string;
  job_title: string;
  company: string;
  linkedin_url: string;
  avatar_url: string;
}

interface ProfileSetupProps {
  onComplete: () => void;
}

export const ProfileSetup: React.FC<ProfileSetupProps> = ({ onComplete }) => {
  const { user, loading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Show loading if auth is still loading
  if (loading) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading profile setup...</p>
        </CardContent>
      </Card>
    );
  }

  // Show error if no user found
  if (!user) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <p className="text-red-600 mb-4">Authentication error. Please try logging in again.</p>
          <Button onClick={onComplete} variant="outline">
            Continue without Profile Setup
          </Button>
        </CardContent>
      </Card>
    );
  }
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    full_name: user?.user_metadata?.full_name || '',
    bio: '',
    job_title: '',
    company: '',
    linkedin_url: '',
    avatar_url: '',
  });

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    // Enhanced debugging and validation
    console.log('Avatar upload initiated:', { 
      file: !!file, 
      fileName: file?.name,
      fileSize: file?.size,
      user: !!user, 
      userId: user?.id 
    });

    if (!file) {
      console.error('No file selected');
      toast.error('Please select a file to upload');
      return;
    }

    if (!user) {
      console.error('User not authenticated');
      toast.error('Authentication required. Please log in again.');
      return;
    }

    // File size validation (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // File type validation
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    console.log('Starting avatar upload:', { fileName: file.name, fileSize: file.size, userId: user.id });
    setAvatarUploading(true);
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;
      
      console.log('Upload path:', fileName);

      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      console.log('Upload result:', { error: uploadError, data: uploadData });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      console.log('Public URL data:', data);
      setProfileData(prev => ({ ...prev, avatar_url: data.publicUrl }));
      toast.success('Avatar uploaded successfully!');
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error(`Failed to upload avatar: ${error.message || 'Unknown error'}`);
    } finally {
      setAvatarUploading(false);
    }
  };

  const saveProfile = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          full_name: profileData.full_name,
          bio: profileData.bio,
          job_title: profileData.job_title,
          company: profileData.company,
          linkedin_url: profileData.linkedin_url,
          avatar_url: profileData.avatar_url,
        });

      if (error) throw error;

      toast.success('Profile saved successfully!');
      onComplete();
    } catch (error) {
      toast.error('Failed to save profile');
      console.error('Profile save error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const skipProfile = () => {
    onComplete();
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
        <CardDescription>
          Set up your profile to appear on your certificate and for credential verification
        </CardDescription>
        <Badge variant="secondary" className="mx-auto w-fit">
          Optional - You can skip this step
        </Badge>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Avatar Upload */}
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <Avatar className="w-24 h-24">
              <AvatarImage src={profileData.avatar_url} />
              <AvatarFallback className="text-lg">
                {profileData.full_name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <Button
              size="icon"
              variant="outline"
              className="absolute -bottom-2 -right-2 rounded-full"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarUploading}
              aria-label="Upload profile picture"
            >
              <Camera className="h-4 w-4" />
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarUpload}
            className="hidden"
          />
          <p className="text-sm text-muted-foreground">
            {avatarUploading ? 'Uploading...' : 'Click camera to upload photo'}
          </p>
        </div>

        {/* Profile Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              value={profileData.full_name}
              onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder="Your full name"
            />
          </div>

          <div>
            <Label htmlFor="job_title">Job Title</Label>
            <Input
              id="job_title"
              value={profileData.job_title}
              onChange={(e) => setProfileData(prev => ({ ...prev, job_title: e.target.value }))}
              placeholder="e.g., Data Scientist"
            />
          </div>

          <div>
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              value={profileData.company}
              onChange={(e) => setProfileData(prev => ({ ...prev, company: e.target.value }))}
              placeholder="Your company name"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="linkedin_url">LinkedIn Profile (Optional)</Label>
            <Input
              id="linkedin_url"
              type="url"
              value={profileData.linkedin_url}
              onChange={(e) => setProfileData(prev => ({ ...prev, linkedin_url: e.target.value }))}
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="bio">Bio (Optional)</Label>
            <Textarea
              id="bio"
              value={profileData.bio}
              onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell us a bit about yourself..."
              rows={3}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            onClick={saveProfile}
            disabled={isLoading || !profileData.full_name}
            className="flex-1"
          >
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Profile'}
          </Button>
          <Button
            variant="outline"
            onClick={skipProfile}
            className="flex-1"
          >
            Skip for Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};